import { useState, useRef, useEffect } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { Calculator } from 'lucide-react';
import CPCalc from './CPCalc';

const CALCULATORS = [
  { id: 'cp-calc', name: 'CP Calc', icon: Calculator },
];

export default function CalculatorSwitcher() {
  const { colorMode } = useColorMode();
  const [isOpen, setIsOpen] = useState(false);
  const [openCalcId, setOpenCalcId] = useState(null);
  const menuRef = useRef(null);

  const isDark = colorMode === 'dark';
  const theme = {
    btnOpenBg: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(31, 78, 121, 0.1)',
    btnHoverBg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(31, 78, 121, 0.06)',
    iconColor: isDark ? '#f3f4f6' : 'currentColor',
    menuBg: isDark ? '#1f2937' : 'white',
    menuBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
    headerBorder: isDark ? '#374151' : '#f0f0f0',
    linkHoverBg: isDark ? '#374151' : '#f8fafc',
    linkText: isDark ? '#f3f4f6' : '#1f2937',
    linkSubtext: '#9ca3af',
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  function handleCalcClick(calcId) {
    setIsOpen(false);
    setOpenCalcId(calcId);
  }

  return (
    <>
      <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Calculators"
          aria-expanded={isOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.25rem',
            height: '2.25rem',
            borderRadius: '0.5rem',
            border: 'none',
            backgroundColor: isOpen ? theme.btnOpenBg : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            padding: 0,
          }}
          onMouseOver={(e) => {
            if (!isOpen) e.currentTarget.style.backgroundColor = theme.btnHoverBg;
          }}
          onMouseOut={(e) => {
            if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Calculator size={20} color={theme.iconColor} />
        </button>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '0.5rem',
              minWidth: '220px',
              backgroundColor: theme.menuBg,
              borderRadius: '0.75rem',
              boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.08)',
              border: `1px solid ${theme.menuBorder}`,
              zIndex: 9999,
              overflow: 'hidden',
              animation: 'fhl-calc-menu-enter 0.15s ease-out',
            }}
          >
            <div style={{
              padding: '0.75rem 1rem 0.5rem',
              borderBottom: `1px solid ${theme.headerBorder}`,
            }}>
              <span style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: theme.linkSubtext,
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}>
                Calculators
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.5rem',
              padding: '1rem',
            }}>
              {CALCULATORS.map((calc) => {
                const IconComponent = calc.icon;
                return (
                  <button
                    key={calc.id}
                    onClick={() => handleCalcClick(calc.id)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 0.25rem',
                      background: 'none',
                      border: 'none',
                      color: theme.linkText,
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.1s',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = theme.linkHoverBg; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '3.5rem',
                      height: '3.5rem',
                      flexShrink: 0,
                    }}>
                      <IconComponent size={30} color={theme.iconColor} />
                    </span>
                    <div style={{ fontSize: '0.75rem', fontWeight: 500, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.1 }}>
                      {calc.name}
                    </div>
                  </button>
                );
              })}
            </div>

            <style>{`
              @keyframes fhl-calc-menu-enter {
                from { opacity: 0; transform: translateY(-4px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </div>
        )}
      </div>

      <CPCalc isOpen={openCalcId === 'cp-calc'} onClose={() => setOpenCalcId(null)} />
    </>
  );
}
